import React, { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Grid, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { ErrorBoundary, FormField, CardWrapper, useHookForm } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../Icons/arrow-right';
import { convertingApplicationEmail } from './funcConvertingApplicationEmail';
import AdditionalEmail from '../../components/AdditionalEmail';
import { IEventsStepProps } from '../../interfaceSteps';
import { EOptions, step5Options } from './step5Options';
import { IStep6Form } from './formInterfaces';
import { schema } from './schema';
import ContactApplicationTitle from '../../components/ContactApplicationTitle';
import JobDeliveryMethodSelector from '../../components/JobDeliveryMethodSelector';
import { useGetUserInfoQuery } from '../../../../services/serviceUserInfo';
import { ServerFieldErrors, useServerErrors } from '../../useServerErrors';

// Logged-in posters' contact fields mirror their BevNET profile and are read-only
// (NOT `disabled` — react-hook-form drops disabled fields from the submitted values).
const READONLY_INPUT_STYLE = { backgroundColor: 'rgba(0, 0, 0, 0.06)', cursor: 'not-allowed' };
const PROFILE_ACCOUNT_URL = 'https://account.bevnet.com/user/account';

interface IStep5Props extends IEventsStepProps {
  setStep6: (value: IStep6Form) => void,
  defaultValue: IStep6Form,
  serverErrors?: ServerFieldErrors
}

export const Step6 = memo(({
  Nav,
  onNext,
  onBack,
  setStep6,
  defaultValue,
  serverErrors
}:IStep5Props) => {
  const [isShowUrl, setIsShowUrl] = useState(false);
  const [changedFields, setChangedFields] = useState<IStep6Form | null>(null);

  const {
    useFieldArray,
    setValue,
    register,
    handleSubmit,
    getValues,
    setError,
    control,
    errors
  } = useHookForm<IStep6Form>({
    schema,
    showChangedFields: (a) => {
      setChangedFields(a);
    }
  });

  useServerErrors(setError, serverErrors);

  // When logged in, the poster's contact fields mirror the BevNET profile and are
  // read-only; anything else stays editable so nobody gets stuck.
  //
  // Lock only when the handed-down value EQUALS the profile value — that is what proves
  // it came from the profile. A weaker "profile has a value AND the field has a value"
  // gate is wrong in two ways this equality closes:
  //   - Empty profile + a value the USER typed (they fill a field, advance, navigate
  //     back, and onSubmit has persisted it into `defaultValue`): `'' === typed` is false,
  //     so it stays editable (D-24094 §15/M2).
  //   - Populated profile + a DIFFERENT restored-draft value ("Jane" profile, "Pat" draft
  //     saved before the profile changed): a populated profile proves a value exists, not
  //     that this one came from it. `'Pat' === 'Jane'` is false, so it stays editable
  //     (D-24094 §15/M1). The `!!currentUserInfo?.*` guard keeps an empty profile ('' === '')
  //     from locking an empty field.
  // Never the live form value — that would flip readOnly on the first keystroke.
  const { data: currentUserInfo } = useGetUserInfoQuery();
  const loggedIn = Boolean(currentUserInfo?.isLoggedIn);
  const lockFirstName = loggedIn && !!currentUserInfo?.firstName && defaultValue.contactFirstName === currentUserInfo.firstName;
  const lockLastName = loggedIn && !!currentUserInfo?.lastName && defaultValue.contactLastName === currentUserInfo.lastName;
  const lockEmail = loggedIn && !!currentUserInfo?.email && defaultValue.email === currentUserInfo.email;
  const hasLockedProfileFields = lockFirstName || lockLastName || lockEmail;

  const goBack = () => {
    if (onBack) {
      if (changedFields && (JSON.stringify(defaultValue).toString() !== JSON.stringify(changedFields))) {
        setStep6({
          ...changedFields,
          applicationEmail: convertingApplicationEmail(changedFields.tempAdditionalEmails),
          applicationUrlEnabled: !!isShowUrl
        });
      }
      onBack();
    }
  };

  const { fields, append, remove } = useFieldArray({
    name: 'tempAdditionalEmails',
    control
  });

  const increase = useCallback(() => {
    append({
      email: null
    });
  }, [append]);

  const decrease = (id: number) => {
    if (fields.length !== 0) {
      remove(id);
    }
  };

  const onSubmit = (data: IStep6Form) => {
    if (data && onNext) {
      data.applicationEmail = convertingApplicationEmail(data.tempAdditionalEmails);
      data.applicationUrlEnabled = data.applicationUrlEnabled !== '0';
      setStep6(data);
      onNext();
    }
  };

  const handleDeliveryMethodChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event && !Array.isArray(event)) {
      setValue('applicationUrlEnabled', event.target.value);
      setValue('applicationUrl', null);
      if (event.target.value === EOptions.APPLICATIONS) {
        increase();
        setIsShowUrl(false);
        setValue('applicationUrl', null);
        setValue('showEmails', true);
        return;
      }
      setValue('tempAdditionalEmails', []);
      setValue('showEmails', false);
      setIsShowUrl(true);
    }
  };

  useEffect(() => {
    Object.keys(defaultValue).forEach((item) => {
      if (item !== 'tempAdditionalEmails' && item !== 'applicationURLEnabled') {
        // @ts-ignore
        setValue(item, defaultValue[item]);
      }
    });
    if (!defaultValue.applicationUrlEnabled) {
      setValue('applicationUrlEnabled', step5Options[0].value);
    }
    if (defaultValue.applicationUrlEnabled) {
      setValue('applicationUrlEnabled', step5Options[1].value);
    }

    setIsShowUrl(defaultValue.applicationUrlEnabled as boolean);

    setValue('tempAdditionalEmails', []);
    let emailStr = '';
    if (defaultValue?.tempAdditionalEmails?.length > 0) {
      defaultValue.tempAdditionalEmails.forEach((item) => {
        if (item.email) {
          append(item);
          emailStr += `${item.email}; `;
        }
      });
    }
    if (!defaultValue.applicationUrlEnabled && defaultValue.applicationEmail?.length === 0) {
      append({
        email: null
      });
    }
    if (defaultValue?.showEmails === null) {
      setValue('showEmails', false);
    }

    setValue('applicationEmail', emailStr);
  }, [defaultValue]);

  return (
    <CardWrapper
      classNameForInternalBlock="cpg-step-card"
      scrollOptions={{
        isErrorsAutoScrollPage: errors
      }}
    >
      <form
        data-cy="step6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <input
          style={{ display: 'none' }}
          type="checkbox"
          {...register('showEmails')}
        />
        <input
          type="hidden"
          {...register('applicationEmail')}
        />

        <ContactApplicationTitle />

        <Grid
          container
          columnSpacing={2}
          rowSpacing={1}
        >
          <Grid
            item
            xs={12}
            sm={6}
          >
            <FormField
              isRequired
              extraProps={{ ...register('contactFirstName') }}
              customInputProps={lockFirstName ? { readOnly: true, style: READONLY_INPUT_STYLE } : undefined}
              endAdornment={lockFirstName ? <LockOutlinedIcon fontSize="small" sx={{ color: 'action.disabled' }} /> : undefined}
              errors={errors}
              label="Your First Name"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            <FormField
              isRequired
              extraProps={{ ...register('contactLastName') }}
              customInputProps={lockLastName ? { readOnly: true, style: READONLY_INPUT_STYLE } : undefined}
              endAdornment={lockLastName ? <LockOutlinedIcon fontSize="small" sx={{ color: 'action.disabled' }} /> : undefined}
              errors={errors}
              label="Your Last Name"
            />
          </Grid>
        </Grid>
        <FormField
          isRequired
          extraProps={{ ...register('email') }}
          customInputProps={lockEmail ? { readOnly: true, style: READONLY_INPUT_STYLE } : undefined}
          endAdornment={lockEmail ? <LockOutlinedIcon fontSize="small" sx={{ color: 'action.disabled' }} /> : undefined}
          errors={errors}
          label="Your Email"
        />

        {hasLockedProfileFields && (
          <Alert
            severity="info"
            icon={<LockOutlinedIcon fontSize="inherit" />}
            sx={{ mb: 2, alignItems: 'center' }}
          >
            Fields marked with a lock are pulled from your BevNET CPG Media Account and can’t be edited here.{' '}
            <a href={PROFILE_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700 }}>
              Update your account →
            </a>
          </Alert>
        )}

        <Typography
          variant="h6"
          sx={{
            mb: 2
          }}
        >
          Job Application Delivery Method
        </Typography>

        <JobDeliveryMethodSelector
          options={step5Options}
          value={(getValues('applicationUrlEnabled') || step5Options[0].value).toString()}
          onChange={handleDeliveryMethodChange}
        />

        {!isShowUrl && (
        <Typography
          variant="body2"
          sx={{
            mb: 2
          }}
        >
          Applications for this job will be sent to:
        </Typography>
        )}

        <ErrorBoundary>
          {!isShowUrl && fields && fields.length ? fields.map((item, index) => (
            <AdditionalEmail
              generalFieldsLength={fields.length}
              id={index}
              register={register}
              errors={{
                ...(errors?.tempAdditionalEmails) && ({ ...errors.tempAdditionalEmails[index] })
              }}
              decrease={decrease}
              key={item.id}
              nameField={`tempAdditionalEmails.${index}.email` as const}
            />
          )) : null}
        </ErrorBoundary>

        <FormField
          isRequired
          extraProps={{ ...register('applicationUrl') }}
          placeholder="Url"
          visible={isShowUrl}
          errors={errors}
          label="URL"
        />

        {!isShowUrl && (
        <Button
          className="cpg-ghost-btn"
          type="button"
          variant="outlined"
          color="info"
          startIcon={<Add />}
          onClick={() => {
            increase();
          }}
          sx={{ mt: 2 }}
        >
          Add additional email
        </Button>
        )}

        <Nav
          nextDisabled={false}
          onBack={goBack}
          textPreviousStep="← Back"
          nextButton={(
            <Button
              endIcon={(<ArrowRightIcon fontSize="small" />)}
              variant="contained"
              type="submit"
              data-cy="nextStep"
            >
              Next step
            </Button>
          )}
        />
      </form>
    </CardWrapper>
  );
});
